/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1669593600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Nov 28 2022 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "salesloft",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Syncing",
          "depth": 0,
          "id": "syncing"
        }, {
          "value": "Sync modes",
          "depth": 1,
          "id": "sync-modes"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      a: "a",
      img: "img",
      ul: "ul",
      li: "li",
      code: "code",
      h3: "h3"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsxs(_components.p, {
        children: ["Salesloft requires an API key. To find the API key, navigate to the ", _jsx(_components.a, {
          href: "https://accounts.salesloft.com/oauth/applications",
          children: "Salesloft Dashboard Settings"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "In the Salesloft settings, an API Key can be created on this page."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-salesloft-1.png",
          alt: ""
        })
      }), "\n", _jsx(_components.p, {
        children: "If an API key has never been created, you can create one here."
      }), "\n", _jsx(_components.h2, {
        id: "syncing",
        children: "Syncing"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing to the following Salesloft resources:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "People"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Accounts"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "sync-modes",
        children: "Sync modes"
      }), "\n", _jsx(_components.p, {
        children: "This integration supports Upsert and Update mode."
      }), "\n", _jsx(_components.p, {
        children: "In the Upsert mode, new people or accounts will be inserted into Salesloft and all attributes will be kept up-to-date within Salesloft."
      }), "\n", _jsx(_components.p, {
        children: "In Update mode, people and accounts will be kept up-to-date, but new entries will not be inserted."
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "Person records can be matched from your source to your Salesloft workspace by the person's email in the Upsert mode."
      }), "\n", _jsx(_components.p, {
        children: "Account records can be matched from your source to your Salesloft workspace by your Salesloft Account Domain or CRM ID in the Upsert mode."
      }), "\n", _jsx(_components.p, {
        children: "In Update mode, records can be matched from your source to your Salesloft workspace by ID."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-salesloft-2.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to Salesloft's default and custom fields. Hightouch automatically detects existing custom fields from your Salesloft account."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-salesloft-3.png",
          alt: ""
        })
      }), "\n", _jsx(_components.p, {
        children: "You can add custom attributes for people and accounts in your Salesloft account."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
